import { memo, useCallback } from "react";
import { useWatch } from "react-hook-form";
import { SimpleRangeSlider } from "@/components/base/simple-range-slider";
import { DisabledLabel, LabelWithTooltip } from "@/components/filters/shared";
import type { useFilters } from "@/providers/filter-provider";
import type { FiltersFormValues } from "@/providers/filter-registry";

type PerformanceSimpleRangeProps = {
  form: ReturnType<typeof useFilters>["form"];
  label: string;
  tooltip: string;
  description?: string;
  minField: keyof FiltersFormValues;
  maxField: keyof FiltersFormValues;
  min: number;
  max: number;
  format?: "number" | "currency" | "percent" | "followers" | "frequency";
  disabled?: boolean;
};

export const PerformanceSimpleRange = memo(
  ({
    form,
    label,
    tooltip,
    description,
    minField,
    maxField,
    min,
    max,
    format = "number",
    disabled = false,
  }: PerformanceSimpleRangeProps) => {
    // Use useWatch for efficient subscriptions to specific fields
    const minFieldValue = useWatch({ control: form.control, name: minField });
    const maxFieldValue = useWatch({ control: form.control, name: maxField });

    const minValue = Number(minFieldValue) || min;
    const maxValue = Number(maxFieldValue) || max;

    const handleMinChange = useCallback(
      (value: number) =>
        form.setValue(minField, (value === min ? undefined : String(value)) as never, {
          shouldDirty: true,
          shouldTouch: true,
        }),
      [form, minField, min],
    );

    const handleMaxChange = useCallback(
      (value: number) =>
        form.setValue(maxField, (value === max ? undefined : String(value)) as never, {
          shouldDirty: true,
          shouldTouch: true,
        }),
      [form, maxField, max],
    );

    return (
      <div className="flex flex-col gap-2">
        {disabled ? (
          <DisabledLabel label={label} tooltip={tooltip} description={description} />
        ) : (
          <LabelWithTooltip label={label} tooltip={tooltip} description={description} />
        )}
        <SimpleRangeSlider
          min={min}
          max={max}
          minValue={Math.min(minValue, maxValue)}
          maxValue={Math.max(maxValue, minValue)}
          onMinChange={handleMinChange}
          onMaxChange={handleMaxChange}
          format={format}
          disabled={disabled}
        />
      </div>
    );
  },
);
